import React, { FC, useState } from "react";
import { MatterRelationEditor } from "../matter-relation-editor";
import { VpdList } from "../../vpd-list";
import { DisbursementRow } from "../../disbursements";
import { Disbursement, LibraryDisbursement } from "../../../data-types";
import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableItem, DraggableHandle } from "../../draggable-list";
import { Space } from "antd";

import styles from "./matter-disbursements-editor.css";
import { useDebouncedCallback } from "use-debounce";
import { TaxLabel } from "~/gql/graphql";

type Props = {
  disbursements: Disbursement[];
  onUpdate: (disbursement: Partial<Disbursement>) => void;
  onDelete: (id: number) => void;
  onCreate: (disbursement?: Partial<Disbursement>) => void;
  libraryDisbursements: LibraryDisbursement[];
  onReorder: (activeId: number, overId: number) => void;
  onAutoFocus: () => void;
  taxLevelPercent: number;
  taxLabel: TaxLabel;
  excludeTax: boolean;
  currencySymbol: string;
};
export const MatterDisbursementsEditor: FC<Props> = ({
  disbursements,
  onCreate,
  onDelete,
  onUpdate,
  libraryDisbursements,
  onReorder,
  onAutoFocus,
  taxLevelPercent,
  taxLabel,
  excludeTax,
  currencySymbol,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const debouncedOnUpdate = useDebouncedCallback(
    (disbursement: Partial<Disbursement>) => onUpdate(disbursement),
    600
  );
  const [activeDisbursement, setActiveDisbursement] = useState<Disbursement | null>(null);

  return (
    <MatterRelationEditor
      workspaceContent={
        <Space direction="vertical" size={5} className={(styles as any).container}>
          <DndContext
            onDragStart={({ active }) => {
              setActiveDisbursement(disbursements.find((p) => p.id.toString() === active.id)!);
            }}
            onDragEnd={({ active, over }) =>
              over && onReorder(parseInt(active.id), parseInt(over.id))
            }
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={disbursements.map((p) => p.id.toString())}
              strategy={verticalListSortingStrategy}
            >
              <VpdList
                itemKeyProperty="id"
                itemTextProperty="text"
                loading={false}
                split={false}
                items={disbursements}
                renderItem={(item: Disbursement) => {
                  const isAutoFocused = item?.autoFocus ?? false;
                  return (
                    <SortableItem id={item.id.toString()} key={item.id}>
                      {({ attributes, listeners }) => (
                        <VpdList.Item
                          key={item.id.toString()}
                          content={
                            <DisbursementRow
                              autoFocus={isAutoFocused}
                              onAutoFocus={onAutoFocus}
                              disbursement={item}
                              libraryDisbursements={libraryDisbursements}
                              onUpdate={debouncedOnUpdate}
                              onDelete={onDelete}
                              taxLevelPercent={taxLevelPercent}
                              taxLabel={taxLabel}
                              currencySymbol={currencySymbol}
                              excludeTax={excludeTax}
                            />
                          }
                          contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                        />
                      )}
                    </SortableItem>
                  );
                }}
                listStyle="primary"
                labels={{
                  emptyText: "No items available.",
                  loadingText: "Loading items...",
                }}
              />
            </SortableContext>
            <DragOverlay>
              {activeDisbursement ? (
                <VpdList
                  itemKeyProperty="id"
                  itemTextProperty="text"
                  loading={false}
                  split={false}
                  items={[activeDisbursement]}
                  renderItem={(item: Disbursement) => (
                    <VpdList.Item
                      key="rubbish"
                      content={
                        <DisbursementRow
                          disbursement={item}
                          libraryDisbursements={libraryDisbursements}
                          onUpdate={onUpdate}
                          onDelete={onDelete}
                          taxLevelPercent={taxLevelPercent}
                          taxLabel={taxLabel}
                          currencySymbol={currencySymbol}
                          excludeTax={excludeTax}
                        />
                      }
                      contentPrefix={<DraggableHandle />}
                    />
                  )}
                  listStyle="primary"
                  labels={{
                    emptyText: "No items available.",
                    loadingText: "Loading items...",
                  }}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </Space>
      }
      addNew={() => onCreate()}
      showAddNew={true}
    />
  );
};
